/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import {
  CheckCircle2,
  Factory,
  Recycle,
  Fuel,
  Building2,
  Landmark,
} from 'lucide-react';

const industries = [
  {
    icon: Recycle,
    title: 'Tyre Recycling Facilities',
  },
  {
    icon: Factory,
    title: 'Waste Management Companies',
  },
  {
    icon: Fuel,
    title: 'Fuel Distributors and Traders',
  },
  {
    icon: Building2,
    title: 'Industrial Groups Exploring Circular Economy Solutions',
  },
  {
    icon: Landmark,
    title: 'Government and Municipal Waste Programs',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="relative bg-slate-900 py-24 md:py-32 overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/20 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            Your Business Case, Our Engineering Network
          </motion.h1>
        </div>
      </section>

      {/* Section 1: Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div {...fadeUp}>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              VERIGREEN is an industrial solutions provider specializing in
              thermal conversion technology — pyrolysis and distillation
              equipment that transforms waste tyres and plastics into fuel oil,
              carbon black, and steel wire.
            </p>
            <p className="mt-6 text-lg md:text-xl text-slate-900 font-semibold leading-relaxed">
              We don't sell equipment first. We build your business case first.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 2: How We Work */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-8"
            {...fadeUp}
          >
            How We Work
          </motion.h2>
          <motion.p
            className="text-lg text-slate-600 leading-relaxed"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
          >
            Before any equipment conversation, we analyze your operation:
            feedstock availability, local fuel market prices, regulatory
            requirements, and expected ROI. You receive a custom financial model
            — including yield projections, revenue forecasts, and payback
            timeline — before you see a single invoice.
          </motion.p>
        </div>
      </section>

      {/* Section 3: Our Engineering Network */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-8"
            {...fadeUp}
          >
            Our Engineering Network
          </motion.h2>
          <motion.p
            className="text-lg text-slate-600 leading-relaxed mb-10"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
          >
            Based in Henan, China — home to the world's most concentrated cluster
            of thermal conversion equipment manufacturers — we maintain direct
            relationships with 10+ certified production facilities. This means:
          </motion.p>

          <div className="space-y-6">
            <motion.div
              className="flex gap-4 items-start"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
            >
              <CheckCircle2 className="w-6 h-6 text-[#00bf63] mt-0.5 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  Equipment matching
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  We identify the right plant configuration for your specific
                  feedstock, capacity, and budget — not just the most expensive
                  one.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="flex gap-4 items-start"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.3 }}
            >
              <CheckCircle2 className="w-6 h-6 text-[#00bf63] mt-0.5 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  Quality verification
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Every unit goes through our inspection checklist before
                  shipment. We document everything.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="flex gap-4 items-start"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.4 }}
            >
              <CheckCircle2 className="w-6 h-6 text-[#00bf63] mt-0.5 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  End-to-end coordination
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  From factory floor to your facility — shipping, customs
                  documentation, installation liaison, and commissioning support.
                  One point of contact throughout.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 4: Our Philosophy */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-8"
            {...fadeUp}
          >
            Our Philosophy
          </motion.h2>
          <motion.p
            className="text-lg text-slate-600 leading-relaxed mb-6"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
          >
            The waste-to-energy industry has a trust problem. Too many
            middlemen, too many promises, too little transparency. We exist to
            solve that.
          </motion.p>
          <motion.p
            className="text-lg text-slate-600 leading-relaxed"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
          >
            Every recommendation we make is backed by numbers. Every claim is
            verifiable. And if we don't think thermal conversion is the right
            investment for your situation, we'll tell you that upfront.
          </motion.p>
        </div>
      </section>

      {/* Section 5: Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center"
            {...fadeUp}
          >
            Industries We Serve
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-lg bg-[#00bf63]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#00bf63]" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {item.title}
                  </h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
